import { useState } from 'react'
import { useApp } from '../components/AppContext'
import { mileageAllowance, homeOfficeAllowance, fmt } from '../lib/tax'
import { Car, Home, Plus, Trash2 } from 'lucide-react'

export default function Mileage() {
  const { mileageTrips, addTrip, removeTrip, homeOffice, setHomeOffice } = useApp()
  const [form, setForm] = useState({ date:'', from:'Home', to:'', miles:'', purpose:'' })
  const [showForm, setShowForm] = useState(false)

  const totalMiles = mileageTrips.reduce((s,t) => s + Number(t.miles), 0)
  const mileAdj    = mileageAllowance(totalMiles)
  const homeAdj    = homeOfficeAllowance(homeOffice.months, homeOffice.hoursPerMonth)
  const total      = mileAdj + homeAdj

  function submit() {
    if (!form.date || !form.to || !form.miles) return
    addTrip({ ...form, miles: Number(form.miles) })
    setForm({ date:'', from:'Home', to:'', miles:'', purpose:'' })
    setShowForm(false)
  }

  const homeRate = homeOffice.hoursPerMonth >= 101 ? 26 : homeOffice.hoursPerMonth >= 51 ? 18 : 10

  return (
    <div className="fade-up">
      <div className="page-head">
        <h1 className="page-title">Mileage & Home Office</h1>
        <p className="page-sub">Flat-rate allowances set by HMRC — no receipts needed</p>
      </div>

      {/* Mileage callout for high-mileage users */}
      <div style={{ background:'var(--teal-glow)', border:'1px solid rgba(0,212,170,.2)', borderRadius:'var(--r)', padding:'14px 18px', marginBottom:20, display:'flex', gap:12, alignItems:'flex-start' }}>
        <span style={{ fontSize:20, flexShrink:0 }}>🚗</span>
        <div>
          <div style={{ fontWeight:600, fontSize:13, color:'var(--white)', marginBottom:3 }}>HMRC Mileage Allowance — the most missed tax saving</div>
          <div style={{ fontSize:12, color:'var(--slate)', lineHeight:1.65 }}>
            45p per mile for the first 10,000 business miles, then 25p. If you drive for work — Uber, deliveries, client visits, site visits — every mile counts. Log them here and Quarterly deducts them automatically from your tax bill.
          </div>
        </div>
      </div>

      <div className="g3" style={{ marginBottom:22 }}>
        <div className="stat">
          <div className="stat-label">Mileage Allowance</div>
          <div className="stat-value teal">{fmt(mileAdj)}</div>
          <div className="stat-sub">{totalMiles} miles · HMRC 45p/25p rate</div>
        </div>
        <div className="stat">
          <div className="stat-label">Home Office Allowance</div>
          <div className="stat-value teal">{fmt(homeAdj)}</div>
          <div className="stat-sub">{homeOffice.months} months @ £{homeRate}/month</div>
        </div>
        <div className="stat" style={{ background:'var(--teal-glow)', borderColor:'rgba(0,212,170,.2)' }}>
          <div className="stat-label">Total Added to Deductions</div>
          <div className="stat-value teal">{fmt(total)}</div>
          <div className="stat-sub">Reduces your tax bill</div>
        </div>
      </div>

      <div className="g2">
        {/* Mileage */}
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontWeight:600 }}>
              <Car size={16} color="var(--teal)" /> Business Trips
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>
              <Plus size={13} /> Add Trip
            </button>
          </div>

          <div className="alert alert-teal" style={{ marginBottom:14, fontSize:12 }}>
            HMRC rate: <strong>45p/mile</strong> (first 10,000) · <strong>25p/mile</strong> thereafter
          </div>

          {showForm && (
            <div className="card" style={{ marginBottom:14 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div className="g2">
                  <div className="field"><label>Date</label><input type="date" value={form.date} onChange={e => setForm(p=>({...p,date:e.target.value}))} /></div>
                  <div className="field"><label>Miles</label><input type="number" placeholder="0" value={form.miles} onChange={e => setForm(p=>({...p,miles:e.target.value}))} /></div>
                </div>
                <div className="field"><label>From</label><input value={form.from} onChange={e => setForm(p=>({...p,from:e.target.value}))} /></div>
                <div className="field"><label>To</label><input value={form.to} onChange={e => setForm(p=>({...p,to:e.target.value}))} placeholder="Destination / client" /></div>
                <div className="field"><label>Purpose</label><input value={form.purpose} onChange={e => setForm(p=>({...p,purpose:e.target.value}))} placeholder="Client meeting, training..." /></div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-primary btn-sm" onClick={submit}>Save</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {mileageTrips.map(t => (
              <div key={t.id} className="card card-sm" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:500 }}>{t.from} → {t.to}</div>
                  <div style={{ fontSize:11, color:'var(--slate-dim)' }}>{t.date} · {t.purpose}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--teal)' }}>{fmt(mileageAllowance(t.miles))}</div>
                    <div style={{ fontSize:11, color:'var(--slate-dim)' }}>{t.miles} miles</div>
                  </div>
                  <button onClick={() => removeTrip(t.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--slate-dim)', padding:4 }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Home Office */}
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, fontWeight:600, marginBottom:14 }}>
            <Home size={16} color="var(--teal)" /> Home Office
          </div>

          <div className="card" style={{ marginBottom:14 }}>
            <div style={{ fontSize:13, color:'var(--slate)', marginBottom:18 }}>
              HMRC simplified flat rate — based on hours worked from home. No receipts needed.
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div className="field">
                <label>Months worked from home this quarter</label>
                <input type="number" min="0" max="3" value={homeOffice.months} onChange={e => setHomeOffice(p=>({...p,months:Number(e.target.value)}))} />
              </div>
              <div className="field">
                <label>Average hours worked from home per month</label>
                <select value={homeOffice.hoursPerMonth} onChange={e => setHomeOffice(p=>({...p,hoursPerMonth:Number(e.target.value)}))}>
                  <option value={25}>25–50 hours/month (£10/month)</option>
                  <option value={51}>51–100 hours/month (£18/month)</option>
                  <option value={101}>101+ hours/month (£26/month)</option>
                </select>
              </div>
              <div style={{ background:'var(--teal-glow)', borderRadius:8, padding:'14px 16px', border:'1px solid rgba(0,212,170,.2)' }}>
                {[
                  { label:'Rate per month', val:`£${homeRate}` },
                  { label:'Months claimed', val:homeOffice.months },
                  { label:'Total allowance', val:fmt(homeAdj), bold:true },
                ].map(r => (
                  <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize: r.bold ? 14 : 12 }}>
                    <span style={{ color:'var(--slate)' }}>{r.label}</span>
                    <span style={{ fontWeight: r.bold ? 700 : 500, color: r.bold ? 'var(--teal)' : 'var(--white)' }}>{r.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Incorporation trigger */}
          <div className="alert alert-amber" style={{ flexDirection:'column', alignItems:'flex-start', gap:6 }}>
            <div style={{ fontWeight:600 }}>💡 Incorporation tip</div>
            <div style={{ fontSize:12, lineHeight:1.5 }}>
              Once your annual profit consistently exceeds £50k, incorporating as a Ltd company typically saves significant tax. Quarterly will flag this automatically when you hit the threshold.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
